<script lang="ts">
	import CustomFieldDisplay from '$lib/component/custom-fields/CustomFieldDisplay.svelte';
	import { minutesFromMidnightToLocalizedTime } from '$lib/helper/date';
	import { formatDate, t } from '$lib/stores/i18n';
	import Calendar from '$lib/svg/calendar.svg.svelte';
	import Clock from '$lib/svg/clock.svg.svelte';
	import CustomerSpeaking from '$lib/svg/customerSpeaking.svg.svelte';
	import Offer from '$lib/svg/offer.svg.svelte';
	import People from '$lib/svg/people.svg.svelte';
	import Person from '$lib/svg/person.svg.svelte';
	import { slide } from 'svelte/transition';
	import {
		hasUniqueDateChoice,
		hasUniqueOfferChoice,
		hasUniquePaxChoice,
		hasUniqueSession,
		widgetCurrentStep
	} from '../flow';
	import type { WidgetMyBooking } from '../helper/myBooking';
	import { widgetContent, type WidgetBooking } from '../stores';
	import RemainingTimeBeforeExpiration from './RemainingTimeBeforeExpiration.svelte';
	import WidgetEditButton from './WidgetEditButton.svelte';

	interface Props {
		booking: WidgetBooking | WidgetMyBooking;
		editable?: boolean;
		hideCustomerRequest?: boolean;
	}

	let { booking, editable = true, hideCustomerRequest = false }: Props = $props();
</script>

<!-- Booking Details -->
<div class="text-left flex flex-col gap-4 my-4 w-full">
	<div class="bg-widget-primary/10 p-4 rounded-md shadow-md">
		<div class="font-bold mb-2 text-lg">{$t('widget-external.summary.offer')}</div>
		<div class="hidden lg:block">
			<RemainingTimeBeforeExpiration />
		</div>
		<div class="grid grid-cols-2 gap-4">
			{#if booking.date}
				<div class="flex items-center gap-2">
					<Calendar className="h-4 w-4 flex-none" />
					{$t('widget-external.summary.date')}
				</div>
				<div class="text-right">
					<WidgetEditButton editable={editable && !$hasUniqueDateChoice} href={'./hub'}>
						{formatDate(booking.date)}
					</WidgetEditButton>
				</div>
			{/if}
			{#if booking.pax}
				{@const offer =
					'offer' in booking
						? booking.offer
						: booking.offers
							? booking.offers[0]
							: undefined}
				{#if !offer || offer.minPax !== offer.maxPax}
					<div class="flex items-center gap-2">
						<People className="h-4 w-4 flex-none" />
						{$t('widget-external.summary.pax')}
					</div>
					<div class="flex justify-end items-center gap-2">
						<WidgetEditButton editable={editable && !hasUniquePaxChoice()} href="./pax">
							{booking.pax}
						</WidgetEditButton>
					</div>
				{/if}
			{/if}

			{#if booking.offer || ('offers' in booking && booking.offers.length > 0)}
				{@const firstOffer = booking.offer ?? booking.offers[0]}
				{@const firstImage =
					'offers' in booking
						? booking.offers.find((offer) => offer.image)?.image
						: firstOffer.image}
				{#if firstImage}
					<img
						src={firstImage}
						alt={firstOffer.name}
						class="col-span-full block w-full object-cover rounded-lg aspect-video shrink-0 max-h-60 -mb-2"
					/>
				{/if}
				<div class="flex items-start gap-2">
					<div class="hidden items-center gap-2">
						<Offer className="h-4 w-4 flex-none" />
						{$t('widget-external.step.offer')}
					</div>
				</div>

				<div class="text-right flex flex-col gap-3">
					<WidgetEditButton
						editable={editable && !$hasUniqueOfferChoice()}
						href={'./hub'}
					>
						{#if 'offers' in booking}
							{booking.offers.map((offer) => offer.name).join(', ')}
						{:else}
							{booking.offer?.name}
						{/if}
					</WidgetEditButton>
				</div>
			{/if}

			{#if booking.sessions.length > 0}
				<div transition:slide={{ duration: 1000, axis: 'y' }}>
					<div class="flex items-center gap-2">
						<Clock className="h-4 w-4 flex-none" />
						{$t('widget-external.summary.sessions')}
					</div>
				</div>
				<div transition:slide={{ duration: 1000, axis: 'y' }} class="flex justify-end">
					<div class="text-right flex flex-col gap-2 items-end">
						{#each booking.sessions.sort((a, b) => a.startAt - b.startAt) as session}
							<div class="grid grid-cols-3 gap-2 w-full">
								<div class="text-left">
									<div class="text-widget-primary dark:text-widget-primary-dark">
										{minutesFromMidnightToLocalizedTime(session.startAt)}
									</div>
									<div class="opacity-60 text-sm">
										{minutesFromMidnightToLocalizedTime(session.endAt)}
									</div>
								</div>
								<div class="col-span-2">
									<div class="truncate">
										{$widgetContent.activities.find(
											(activity) => activity.id === session.activityId
										)?.name ?? ''}
									</div>
									<div class="text-sm opacity-60 truncate">
										{$widgetContent.sessions[session.id]?.name ??
											booking.sessions.find((s) => s.id === session.id)?.name}
									</div>
								</div>
							</div>
						{/each}
					</div>
					<WidgetEditButton editable={editable && !$hasUniqueSession} href={'./hub'} />
				</div>
			{/if}

			{#if booking.customer.email && $widgetCurrentStep !== 'customer'}
				<div transition:slide={{ duration: 1000, axis: 'y' }}>
					<div class="flex items-center gap-2">
						<Person className="h-4 w-4 flex-none" />
						{$t('widget-external.summary.customer')}
					</div>
				</div>
				<div class="text-right" transition:slide={{ duration: 1000, axis: 'y' }}>
					<div>
						<WidgetEditButton {editable} href="./customer">
							<div class="truncate">
								<span class="truncate">
									{booking.customer.firstName}
								</span>
								<span class="truncate">
									{booking.customer.lastName}
								</span>
							</div>
						</WidgetEditButton>
					</div>
					<div class="opacity-60 text-sm text-right">
						<div class="truncate">
							{booking.customer.email}
						</div>
						<div class="truncate">
							{booking.customer.phone}
						</div>
					</div>
				</div>

				{#if booking.customer.customFields}
					{#each Object.keys(booking.customer.customFields) as customFieldId}
						<CustomFieldDisplay
							value={booking.customer.customFields[customFieldId]}
							customField={$widgetContent.customFields.find(
								(cf) => cf.id === customFieldId
							)}
							displayWidget
						/>
					{/each}
				{/if}
				{#if booking.customFields}
					{#each Object.keys(booking.customFields) as customFieldId}
						<CustomFieldDisplay
							value={booking.customFields[customFieldId]}
							customField={$widgetContent.customFields.find(
								(cf) => cf.id === customFieldId
							)}
							displayWidget
						/>
					{/each}
				{/if}
			{/if}

			{#if !hideCustomerRequest && booking.note}
				<div>
					<div class="flex items-center gap-2 truncate">
						<CustomerSpeaking className="h-4 w-4 flex-none" />
						<span class="truncate">{$t('widget-external.summary.note')}</span>
					</div>
				</div>
				<div class="text-right text-pretty text-sm opacity-60">
					{booking.note}
				</div>
			{/if}
		</div>
	</div>
</div>
